import React, {useEffect, useState} from "react";
import {fetchWithToken, postWithToken, putWithToken} from "../../api/apiBase";
import {useParams} from "react-router-dom";
import {useAuthInfo} from "@propelauth/react";
import {CreateRound, RoundModel} from "./round_model";
import {Separator} from "../ui/separator";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip"
import {Button} from "../ui/button";
import {CardHeader, CardTitle} from "../ui/card";
import {useToast} from "../ui/use-toast";

import {RoundConfigurator_v2} from "./BidRound_v2";


function formatRound(round: RoundModel) {
    return {
        ...round,
        start_date: round.start_date ? round.start_date.split("T")[0] : null,
        end_date: round.end_date ? round.end_date.split("T")[0] : null,
    }
}

export function ListofRounds_v2() {
    const [rounds, SetRounds] = useState<RoundModel[]>([]);
    const {project_id} = useParams();
    const {accessToken} = useAuthInfo();
    const {toast} = useToast();
    const [isRoundDeleted, setIsRoundDeleted] = useState(false);

    const fetchRounds = async () => {
        try {
            const data = await fetchWithToken(
                `/projects/${project_id}/rounds/`,
                accessToken as string
            );
            const formattedRounds = data.map((round: RoundModel) => formatRound(round));
            // console.log(formattedRounds);
            SetRounds(formattedRounds);
            setIsRoundDeleted(false);
        } catch (error) {
            setIsRoundDeleted(false);
        }
    };

    useEffect(() => {
        if (isRoundDeleted) {
            fetchRounds();
            setIsRoundDeleted(false);
        }
    }, [isRoundDeleted]);

    useEffect(() => {
        /**
         * Rounds **SHOULD** come in sorted chronologically.
         * At the start of a project there is only one 'DEFAULT ROUND'
         * ... that sets up the project to be Hidden, except to invited Companies.
         * */
        fetchRounds();
    }, [project_id]);


    const onAddRound = async (after: string) => {
        /**
         * The rules are:
         * if previous round is open-end duration, new round is also open ended
         *
         * if previous round has end-date, new round start date becomes that end-date.
         *
         * if there are two open ended rounds A and B. The user updates the end date of A, that becomes
         * the start date for B.
         * */
        const previousRound = rounds?.find((round) => round.id === after);
        if (!previousRound) return;

        const startDate = previousRound.end_date ? previousRound.end_date : null;
        let end_date = null;
        if (previousRound.index < rounds.length -1){
            const nextround = rounds[previousRound.index +1]
            end_date = nextround.start_date;
        }

        const roundCreation: CreateRound = {
            name: "",
            instructions: previousRound!.instructions,
            start_date: startDate,
            end_date: end_date,
            bid_config: {...previousRound!.bid_config},
            eligibility: previousRound!.eligibility,
            index: (previousRound!.index || 0) + 1,
        };

        postWithToken(
            `/projects/${project_id}/round/${after}/create`,
            JSON.stringify(roundCreation),
            accessToken as string
        )
            .then((newRounds) => {
                const formattedRounds = newRounds.map((round: RoundModel) => formatRound(round));
                SetRounds(formattedRounds);
                setIsRoundDeleted(false);
            })
            .catch((err) => {
                toast({
                title: "Error!",
                description: "Error adding round " + String(err),
                variant: "destructive",
            });
            });
    };

    const handleSaveAllRounds = async () => {
        const hasDateErrors = rounds.some((round) => {
            const startDate = new Date(round?.start_date).getTime();
            const endDate = round.end_date
                ? new Date(round.end_date).getTime()
                : null;

            return endDate !== null && startDate > endDate;
        });
        if (hasDateErrors) {
            toast({
                title: "Error!",
                description: "Start date cannot be greater than end date",
                variant: "destructive",
            });
            return;
        }
        putWithToken(
            `/projects/${project_id}/rounds`,
            JSON.stringify(rounds),
            accessToken as string
        ).then((data) => {
            const formattedRounds = data.map((round: RoundModel) => formatRound(round));
            SetRounds(formattedRounds);

            toast({
                title: "Success!",
                description: "Rounds successfully updated.",
                variant: "default",
            });
        }).catch((err) => {
            toast({
                title: "Error!",
                description: "Error updating rounds." + err,
                variant: "destructive",
            });
        });
    };

    const handleHidePastRounds = () => {
        const activeRounds = rounds.filter(round => {
            return !round.end_date || new Date(round.end_date).getTime() >= new Date().getTime();
        });

        SetRounds(activeRounds);
    }

    return (
        <div className="flex flex-col gap-4 relative">
            <CardHeader>
                <CardTitle>
                    Bidding Round Configuration
                </CardTitle>
                <Separator/>
            </CardHeader>

            {rounds ? (
                <>
                    <div className="absolute top-[3px] right-[110px]">
                    <Button
                        onClick={handleHidePastRounds}
                        variant="outline"
                        className="mr-5"
                    >
                        Hide past rounds
                    </Button>
                    <Button
                        onClick={handleSaveAllRounds}

                        // className="absolute top-[3px] right-[110px]"
                        variant="outline"
                    >
                        Save
                    </Button>
                    </div>
                    <div className={"flex columns-1"}>
                        {/*<div className="w-1/12 relative h-flex border-l-2 border-gray-300">*/}

                        {/*</div>*/}
                        <div className="w-full ">
                            {rounds.map((round, index) => (
                                <div key={round.id}>
                                    <RoundConfigurator_v2
                                        isFirstRound={index === 0}
                                        round={round}
                                        index={index}
                                        setIsRoundDeleted={setIsRoundDeleted}
                                        SetUpdateRounds={SetRounds}

                                    />
                                    <br/>
                                    <div className="flex justify-center">
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <span>
                                                    <Button
                                                        disabled={round.end_date === null || round.end_date === undefined || round.end_date==='' }
                                                        variant="outline"
                                                        onClick={() => onAddRound(round.id)}
                                                    >
                                                        Add Round
                                                    </Button>
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>{!round.end_date
                                                        ? "You must specify an end date for the current round to add more bidding Rounds"
                                                        : "New Round"
                                                    }</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>


                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            ) : (
                <p>No rounds available</p>
            )}
        </div>
    );
}

